import React, { useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { connect, ConnectedProps } from 'react-redux';
import { AlertTypes, DataGridPro, Mui, MuiDataGrid, MuiIcons, setAlert } from '@leap4/frontend';
import { urlService } from '../../routing/urlsService';
import { tableNames } from '../../../common/data/enums';
import { getSavedFilterModel, saveFilterModel } from '../../../utils/filterUtils';
import { dateToStringFormat } from '../../utils/functions';
import { failureLabelFor, ITrackedPublisher, RESPONSIVENESS_LABELS } from './appAdsLines.interface';
import {
    archivePublisher,
    downloadCombinedUpdateListCsv,
    errorMessage,
    fetchPublishersWithCounts,
    setPublisherActive,
} from './appAdsLines.api';
import { ArchivePublisherDialog } from './archivePublisherDialog';
import { ArchivedPublishers } from './archivedPublishers';
import { OnboardingUploadModal } from './onboardingUploadModal';
import { searchRows } from './rowSearch';
import { useWrappingGrid } from './wrappingGrid';
import { FiltersToolbar } from './filtersToolbar';

type GridColDef = MuiDataGrid.GridColDef;

const {
    Box,
    Button,
    Chip,
    IconButton,
    InputAdornment,
    Switch,
    Tabs,
    Tab,
    TextField,
    Tooltip,
    Typography,
} = Mui;
const {
    Add: AddIcon,
    Delete: DeleteIcon,
    Download: DownloadIcon,
    Edit: EditIcon,
    UploadFile: UploadFileIcon,
    Warning: WarningIcon,
    Search: SearchIcon,
} = MuiIcons;

const connector = connect(() => ({}), { setAlert });
type PropsFromRedux = ConnectedProps<typeof connector>;

/**
 * The publishers half of Publisher Lines.
 *
 * Publishers and lines are siblings, not parent and child - two views of the same data - so
 * this holds only the actions that belong to publishers. Anything acting on lines lives on the
 * lines tab, and "Update all" sits above both because it spans them.
 */
const PublishersTab: React.FC<PropsFromRedux> = ({ setAlert }) => {
    const history = useHistory();
    const queryClient = useQueryClient();

    const [filterModel, setFilterModel] = useState(() => getSavedFilterModel(tableNames.TRACKED_PUBLISHERS));
    const [archiving, setArchiving] = useState<ITrackedPublisher | null>(null);
    const [search, setSearch] = useState('');
    const wrapping = useWrappingGrid();
    const [archiveView, setArchiveView] = useState(false);
    const [onboardingOpen, setOnboardingOpen] = useState(false);

    const { data: publishers, isLoading } = useQuery('trackedPublishers', fetchPublishersWithCounts, {
        refetchOnWindowFocus: false,
    });

    const handleFilterChange = (newFilterModel) => {
        setFilterModel(newFilterModel || { items: [] });
        saveFilterModel(tableNames.TRACKED_PUBLISHERS, newFilterModel);
    };

    const archiveMutation = useMutation(archivePublisher, {
        onSuccess: () => {
            setAlert({ msg: 'Publisher archived. Restore it from the Archive tab.', type: AlertTypes.SUCCESS });
            setArchiving(null);
            queryClient.invalidateQueries('oldLines');
            queryClient.invalidateQueries('trackedPublishers');
            queryClient.invalidateQueries('distinctLineSpecs');
            queryClient.invalidateQueries('archivedPublishers');
        },
        onError: (err: any) => setAlert({ msg: `Could not archive: ${errorMessage(err)}`, type: AlertTypes.FAILURE }),
    });

    const combinedCsvMutation = useMutation(downloadCombinedUpdateListCsv, {
        onSuccess: () => queryClient.invalidateQueries('trackedPublishers'),
        onError: (err: any) => setAlert({ msg: `Export failed: ${errorMessage(err)}`, type: AlertTypes.FAILURE }),
    });

    const activeMutation = useMutation(
        ({ id, isActive }: { id: string; isActive: boolean }) => setPublisherActive(id, isActive),
        {
            onSuccess: (_data, { isActive }) => {
                setAlert({
                    msg: isActive
                        ? 'Publisher activated.'
                        : 'Publisher deactivated.',
                    type: AlertTypes.SUCCESS,
                });
                queryClient.invalidateQueries('trackedPublishers');
                queryClient.invalidateQueries('distinctLineSpecs');
                queryClient.invalidateQueries('oldLines');
            },
            onError: (err: any) => setAlert({ msg: errorMessage(err), type: AlertTypes.FAILURE }),
        },
    );

    const { mutate: toggleActive, isLoading: togglingActive } = activeMutation;

    // Fit all columns into the desktop viewport initially. Narrow numeric/status cells
    // take less room; clipped headers retain the grid's tooltip and drag-to-resize behavior.
    const columns: GridColDef[] = useMemo(
        () => [
            {
                field: 'name',
                headerName: 'Publisher',
                flex: 2, minWidth: 100,
                renderCell: (params: any) => (
                    <Button
                        variant="text"
                        onClick={() => history.push(urlService.trackedPublisher(params.row._id))}
                        title={params.row.name}
                        sx={{ textTransform: 'none', justifyContent: 'flex-start', minWidth: 0, maxWidth: '100%' }}
                    >
                        <Box component="span" sx={{ textAlign: 'left', overflowWrap: 'anywhere' }}>
                            {params.row.name}
                        </Box>
                    </Button>
                ),
            },
            { field: 'domain', headerName: 'Domain', flex: 1.8, minWidth: 90 },
            {
                field: 'isActive',
                headerName: 'Active',
                flex: 0.6, minWidth: 58,
                display: 'flex',
                align: 'center',
                headerAlign: 'center',
                // Absent counts as active: publishers written before the field existed have no
                // value stored and are perfectly live.
                valueGetter: (params: any) => params.row.isActive !== false,
                renderCell: (params: any) => (
                    <Tooltip
                        title={
                            params.value
                                ? 'Working this publisher. Switch off to park it — its lines are kept, but it is skipped by checks, bulk actions and the all-lines view.'
                                : 'Parked. Lines and history are kept; switch back on to resume.'
                        }
                    >
                        <Switch
                            size="small"
                            checked={params.value}
                            disabled={togglingActive}
                            onChange={(e) =>
                                toggleActive({
                                    id: params.row._id,
                                    isActive: e.target.checked,
                                })
                            }
                        />
                    </Tooltip>
                ),
            },
            {
                field: 'accountManagers',
                headerName: 'Account managers',
                flex: 1.2, minWidth: 90,
                sortable: false,
                // Free text, several to a publisher - shown as they were typed.
                valueGetter: (params: any) => (params.row.accountManagers || []).join(', '),
            },
            {
                field: 'counts.correct',
                headerName: 'Correct',
                flex: 0.55, minWidth: 48,
                valueGetter: (params: any) => params.row.counts?.correct ?? 0,
                renderCell: (params: any) =>
                    params.value ? <Chip size="small" color="success" variant="outlined" label={params.value} /> : '0',
            },
            {
                field: 'counts.incorrect',
                headerName: 'Wrong type',
                flex: 0.55, minWidth: 48,
                valueGetter: (params: any) => params.row.counts?.incorrect ?? 0,
                renderCell: (params: any) =>
                    params.value ? <Chip size="small" color="warning" variant="outlined" label={params.value} /> : '0',
            },
            {
                field: 'counts.missing',
                headerName: 'Not found',
                flex: 0.55, minWidth: 48,
                valueGetter: (params: any) => params.row.counts?.missing ?? 0,
                renderCell: (params: any) =>
                    params.value ? <Chip size="small" color="error" variant="outlined" label={params.value} /> : '0',
            },
            {
                field: 'counts.unverified',
                headerName: 'Unverified',
                flex: 0.45, minWidth: 42,
                valueGetter: (params: any) => params.row.counts?.unverified ?? 0,
            },
            {
                field: 'counts.toRemove',
                headerName: 'Old & live',
                flex: 0.5, minWidth: 54,
                valueGetter: (params: any) => params.row.counts?.toRemove ?? 0,
                renderCell: (params: any) => {
                    const stale = params.row.counts?.staleRemoved ?? 0;
                    // height:100% so the flex box fills the cell; without it it shrinks to its
                    // contents and rides up to the top, while every other column sits centred.
                    return (
                        <Box display="flex" alignItems="center" gap={0.5} sx={{ height: '100%' }}>
                            <span>{params.value ?? 0}</span>
                            {stale > 0 && (
                                <Tooltip
                                    title={`${stale} old line(s) haven't been checked since removal, so we don't yet know if they're still live.`}
                                >
                                    <WarningIcon fontSize="small" color="warning" />
                                </Tooltip>
                            )}
                        </Box>
                    );
                },
            },
            { field: 'responsiveness', headerName: 'Responsiveness', flex: 0.45, minWidth: 42 },
            {
                field: 'lastCheckedAt',
                headerName: 'Last checked',
                flex: 1.45, minWidth: 110,
                valueGetter: (params: any) =>
                    params.row.lastCheckedAt ? dateToStringFormat(params.row.lastCheckedAt) : 'never',
            },
            {
                field: 'lastCheckErrorKind',
                headerName: 'Read status',
                flex: 0.65, minWidth: 60,
                // A real column rather than an icon: the reason is the actionable part, and it
                // needs to be sortable and filterable so a sweep's failures can be worked
                // through as a list.
                valueGetter: (params: any) => {
                    if (!params.row.lastCheckedAt) return 'Not checked yet';
                    if (!params.row.lastCheckError) return 'OK';
                    return failureLabelFor(params.row.lastCheckErrorLabel, params.row.lastCheckErrorKind);
                },
                renderCell: (params: any) => {
                    const row = params.row as ITrackedPublisher;
                    if (!row.lastCheckedAt) {
                        return <Chip size="small" variant="outlined" label="not checked yet" title="Not checked yet" sx={{ maxWidth: '100%' }} />;
                    }
                    if (!row.lastCheckError) {
                        return <Chip size="small" color="success" variant="outlined" label="OK" />;
                    }
                    return (
                        <Tooltip
                            title={
                                <>
                                    <div>{row.lastCheckErrorAction}</div>
                                    <div style={{ marginTop: 6, opacity: 0.75, fontSize: 11 }}>
                                        {row.lastCheckError}
                                    </div>
                                </>
                            }
                        >
                            <Chip
                                size="small"
                                color="error"
                                variant="outlined"
                                icon={<WarningIcon />}
                                label={params.value}
                                sx={{ maxWidth: '100%' }}
                            />
                        </Tooltip>
                    );
                },
            },
            {
                field: 'updateListLastGeneratedAt',
                headerName: 'List last sent',
                flex: 1.45, minWidth: 110,
                valueGetter: (params: any) =>
                    params.row.updateListLastGeneratedAt
                        ? dateToStringFormat(params.row.updateListLastGeneratedAt)
                        : '-',
            },
            {
                field: 'actions',
                headerName: 'Actions',
                type: 'actions',
                // Fixed, not flex: resizing pins a column's width, so once the others have been
                // resized a flex Actions column would soak up all the space they gave back.
                width: 84,
                renderCell: (params: any) => (
                    <Box>
                        <Tooltip title="Settings">
                            <IconButton
                                size="small"
                                onClick={() => history.push(urlService.editTrackedPublisher(params.row._id))}
                            >
                                <EditIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="Archive publisher">
                            <IconButton size="small" aria-label={`Archive ${params.row.name}`} onClick={(event) => { event.stopPropagation(); setArchiving(params.row); }}>
                                <DeleteIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    </Box>
                ),
            },
        ],
        [history, toggleActive, togglingActive],
    );

    /**
     * Everything on the row that's worth matching, except the counts.
     *
     * The counts are excluded on purpose: they're small integers, so searching "2" would match
     * most of the table through a column nobody searches by. Responsiveness is included as its
     * label rather than its number for the same reason - "slow" is a useful search, "3" isn't.
     *
     * Two fields are excluded for a different reason: they contain the phrase "app-ads.txt" by
     * construction rather than as content, so including them made "app-ads" match almost every
     * row. The raw lastCheckError lists every URL tried, and appAdsTxtUrl is an app-ads.txt URL
     * by definition. Neither loses anything searchable - the error's actionable half is the
     * Read status label, which is still here, and an override's host is almost always the
     * domain, which is also still here.
     */
    const filtered = useMemo(
        () =>
            searchRows(publishers || [], search, (p: any) => [
                p.name,
                p.domain,
                p.contactEmail,
                p.sellerId,
                p.notes,
                (p.accountManagers || []).join(' '),
                RESPONSIVENESS_LABELS[p.responsiveness],
                // The same text the Read status column shows, so what you see is searchable.
                !p.lastCheckedAt
                    ? 'Not checked yet'
                    : !p.lastCheckError
                    ? 'OK'
                    : failureLabelFor(p.lastCheckErrorLabel, p.lastCheckErrorKind),
                p.lastCheckedAt ? dateToStringFormat(p.lastCheckedAt) : 'never',
                p.updateListLastGeneratedAt ? dateToStringFormat(p.updateListLastGeneratedAt) : '',
            ]),
        [publishers, search],
    );

    const busy = combinedCsvMutation.isLoading;

    return (
        <Box>
            <Tabs value={archiveView ? 1 : 0} onChange={(_, value) => setArchiveView(value === 1)} sx={{ mb: 2 }}>
                <Tab label="Publishers" />
                <Tab label="Archive" />
            </Tabs>
            {archiveView ? <ArchivedPublishers /> : <>
                <Box display="flex" gap={1} flexWrap="wrap" mb={1}>
                    <Button
                        variant="contained"
                        size="small"
                        startIcon={<AddIcon />}
                        onClick={() => history.push(urlService.addTrackedPublisher())}
                    >
                        Add publisher
                    </Button>
                    <Button
                        variant="outlined"
                        size="small"
                        startIcon={<DownloadIcon />}
                        disabled={busy}
                        onClick={() => combinedCsvMutation.mutate()}
                    >
                        Generate all updated publisher lists (CSV)
                    </Button>
                    {/* Onboarding a new client: publishers and their lines in one sheet, rather
                        than creating each publisher by hand and importing lines into each. */}
                    <Button
                        variant="outlined"
                        size="small"
                        startIcon={<UploadFileIcon />}
                        disabled={busy}
                        onClick={() => setOnboardingOpen(true)}
                    >
                        Upload publishers and lines
                    </Button>
                </Box>

                <Box display="flex" alignItems="center" gap={2} mb={1}>
                    <TextField
                        size="small"
                        placeholder="Search publishers"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        sx={{ width: 320 }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon fontSize="small" />
                                </InputAdornment>
                            ),
                        }}
                    />
                    {!!search.trim() && (
                        <Typography variant="body2" color="text.secondary">
                            {filtered.length} of {(publishers || []).length} shown
                        </Typography>
                    )}
                </Box>

                <DataGridPro
                    {...wrapping.gridProps}
                    disableColumnPinning
                    rows={filtered}
                    columns={columns}
                    loading={isLoading}
                    autoHeight
                    getRowId={(row) => row._id}
                    // Parked rows stay in the list - that is the only way back - but are dimmed so
                    // the working set reads at a glance.
                    getRowClassName={(params) =>
                        params.row.isActive === false ? 'publisher-row--parked' : ''
                    }
                    sx={{
                        ...wrapping.gridProps.sx,
                        '& .publisher-row--parked': { opacity: 0.5 },
                        '& .MuiDataGrid-cell, & .MuiDataGrid-columnHeader': { px: 0.75 },
                    }}
                    pageSize={100}
                    rowsPerPageOptions={[25, 50, 100]}
                    disableSelectionOnClick
                    filterModel={filterModel}
                    onFilterModelChange={handleFilterChange}
                    // The filter model is saved per browser, so a forgotten filter silently hides
                    // publishers on later visits; the toolbar calls out active filters. The search
                    // box above already covers quick filtering.
                    showToolbar
                    slots={{ toolbar: FiltersToolbar }}
                    slotProps={{ toolbar: { csvOptions: { fileName: 'publishers' } } }}
                    style={{ minHeight: '55vh' }}
                />

                {!isLoading && !(publishers || []).length && (
                    <Box mt={2}>
                        <Typography variant="body2" color="text.secondary">
                            No publishers tracked yet. Add a publisher or import publishers and their lines using the onboarding upload.
                        </Typography>
                    </Box>
                )}

                {/* Distinct from the above: an empty search result isn't an empty seat. */}
                {!isLoading && !!(publishers || []).length && !filtered.length && (
                    <Box mt={2}>
                        <Typography variant="body2" color="text.secondary">
                            No publisher matches "{search}".
                        </Typography>
                    </Box>
                )}

                <ArchivePublisherDialog
                    publisher={archiving}
                    activeLines={archiving?.counts?.total}
                    removalRequests={archiving?.counts?.removed}
                    busy={archiveMutation.isLoading}
                    onCancel={() => setArchiving(null)}
                    onConfirm={() => { if (archiving && !archiveMutation.isLoading) archiveMutation.mutate(archiving._id); }}
                />

                <OnboardingUploadModal
                    open={onboardingOpen}
                    onClose={() => setOnboardingOpen(false)}
                    onImported={(result) => {
                        // No mention of the warnings here: the modal closes on success, so there is
                        // nowhere left to go and look at them. They were on screen before Import.
                        const plural = (n: number, one: string) => `${n} ${n === 1 ? one : `${one}s`}`;
                        setAlert({
                            msg:
                                `Imported ${plural(result.created.publishers, 'publisher')}` +
                                ` and ${plural(result.created.lines, 'line')}.`,
                            type: AlertTypes.SUCCESS,
                        });
                        queryClient.invalidateQueries('trackedPublishers');
                        queryClient.invalidateQueries('distinctLineSpecs');
                    }}
                />
            </>}
        </Box>
    );
};

export default connector(PublishersTab);
